/**
 * Renter search filters (FLEE-346), as data plus pure functions.
 *
 * Filtering, counting and sorting happen in the browser over the vehicles the
 * proximity search already returned. The filter list is `FILTERS` below: one
 * entry per filter (key, kind, order, rule). Every function here reads that
 * list, so a new filter is a new entry, not new branching.
 *
 * The rules that keep the shopper out of dead ends (S6) with a bar that holds
 * still while they filter (FLEE-420):
 * - the search results alone decide which filters and options are offered:
 *   those that leave some vehicles AND remove some;
 * - an option/filter is counted with every OTHER active filter applied, and
 *   one that would leave no vehicles is disabled, not hidden;
 * - range handles snap only to prices present in what is left.
 */
import type { VehicleResult } from "@fm/ui";

export type FilterKind = "multi" | "range" | "toggle" | "choice";

/**
 * What a shopper picked: renter keys for a menu, `[min, max]` per night for a
 * range, `true` for a chip that is on, one step (a number or a key) for a
 * single choice. `false` is a chip the shopper switched
 * off on purpose (it matters only when the chip would otherwise default to on).
 * An absent key means "not set".
 */
export type FilterValue =
  | string[]
  | [number, number]
  | boolean
  | number
  | string;
export type FilterSelection = Record<string, FilterValue>;

export interface FilterContext {
  nights: number;
  /** Pets in the search: 1 or more turns Pet friendly on by default. */
  pets: number;
  currency: string;
}

export type SortKey =
  | "recommended"
  | "price_asc"
  | "price_desc"
  | "newest"
  | "sleeps";

export const SORT_OPTIONS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: "recommended", label: "Recommended" },
  { key: "price_asc", label: "Price low to high" },
  { key: "price_desc", label: "Price high to low" },
  { key: "newest", label: "Newest" },
  { key: "sleeps", label: "Sleeps the most" },
];

export interface MultiOptionInfo {
  key: string;
  name: string;
  description: string | null;
  group: string | null;
  position: number;
}

interface BaseDefinition {
  key: string;
  label: string;
  order: number;
  /** `bar` (default): its own chip. `more`: inside the "More filters" chip. */
  placement?: "bar" | "more";
}

interface MultiDefinition extends BaseDefinition {
  kind: "multi";
  /** URL param holding the comma-separated option keys. */
  param: string;
  groups: ReadonlyArray<{ group: string; label: string }>;
  /** The options a vehicle has; none means it only shows under "All". */
  optionsOf: (vehicle: VehicleResult) => MultiOptionInfo[];
  /** `any`: a vehicle needs one of the picked options. `all`: every one. */
  mode: "any" | "all";
  /** The chip reads "Label (n)" even for one pick. */
  chipCount?: boolean;
  /** List the most common option first (ties keep catalog order). */
  sortByCount?: boolean;
}

interface ChoiceDefinition extends BaseDefinition {
  kind: "choice";
  param: string;
  /** Every step this filter can offer, in display order. */
  choices: ReadonlyArray<{ value: string | number; label: string }>;
  /** What the "no choice" button reads, e.g. "Any length". */
  anyLabel: string;
  matches: (vehicle: VehicleResult, value: string | number) => boolean;
}

interface RangeDefinition extends BaseDefinition {
  kind: "range";
  paramMin: string;
  paramMax: string;
  valueOf: (vehicle: VehicleResult, ctx: FilterContext) => number | null;
}

interface ToggleDefinition extends BaseDefinition {
  kind: "toggle";
  param: string;
  has: (vehicle: VehicleResult) => boolean;
  /** Whether the chip starts on for this search, unless the shopper chose. */
  defaultOn?: (ctx: FilterContext) => boolean;
}

export type FilterDefinition =
  | MultiDefinition
  | RangeDefinition
  | ToggleDefinition
  | ChoiceDefinition;

const round2 = (n: number) => Math.round(n * 100) / 100;

/** The vehicle's total for the stay over the nights: what the card's total implies. */
export function pricePerNight(
  vehicle: VehicleResult,
  nights: number,
): number | null {
  if (typeof vehicle.totalPrice !== "number") return null;
  return round2(vehicle.totalPrice / Math.max(1, nights));
}

/** Amenity keys the server sends, in catalog order, with the labels we show. */
const AMENITY_LABELS: Record<string, string> = {
  generator: "Generator",
  "backup-camera": "Backup camera",
  tv: "TV",
  heater: "Heater",
  awning: "Awning",
  "slide-out": "Slide-out",
  "leveling-jacks": "Leveling jacks",
  solar: "Solar",
  inverter: "Inverter",
  "outdoor-shower": "Outdoor shower",
  "bike-rack": "Bike rack",
  wifi: "Wi-Fi",
};
const AMENITY_KEYS = Object.keys(AMENITY_LABELS);

export const FILTERS: ReadonlyArray<FilterDefinition> = [
  {
    kind: "multi",
    key: "rv_type",
    label: "RV type",
    order: 10,
    param: "rv_type",
    groups: [
      { group: "drivable", label: "Drivable" },
      { group: "towable", label: "Towable" },
      { group: "other", label: "Other" },
    ],
    mode: "any",
    optionsOf: (vehicle) =>
      vehicle.rv_class
        ? [
            {
              key: vehicle.rv_class.renter_key,
              name: vehicle.rv_class.renter_name,
              description: vehicle.rv_class.renter_description,
              group: vehicle.rv_class.group,
              position: vehicle.rv_class.renter_position,
            },
          ]
        : [],
  },
  {
    kind: "range",
    key: "price",
    label: "Price",
    order: 20,
    paramMin: "price_min",
    paramMax: "price_max",
    valueOf: (vehicle, ctx) => pricePerNight(vehicle, ctx.nights),
  },
  {
    kind: "toggle",
    key: "pet_friendly",
    label: "Pet friendly",
    order: 30,
    param: "pet_friendly",
    has: (vehicle) => vehicle.pets_allowed === true,
    defaultOn: (ctx) => ctx.pets >= 1,
  },
  {
    kind: "toggle",
    key: "delivery",
    label: "Delivery",
    order: 40,
    param: "delivery",
    has: (vehicle) => vehicle.delivery_available === true,
  },
  {
    kind: "multi",
    key: "amenities",
    label: "Amenities",
    order: 50,
    param: "amenities",
    groups: [],
    mode: "all",
    chipCount: true,
    sortByCount: true,
    optionsOf: (vehicle) =>
      (vehicle.amenities ?? []).flatMap((key) => {
        const position = AMENITY_KEYS.indexOf(key);
        return position === -1
          ? []
          : [
              {
                key,
                name: AMENITY_LABELS[key]!,
                description: null,
                group: null,
                position,
              },
            ];
      }),
  },
  {
    kind: "toggle",
    key: "festivals",
    label: "Festival friendly",
    order: 60,
    placement: "more",
    param: "festivals",
    has: (vehicle) => vehicle.festivals_events_allowed === true,
  },
  {
    kind: "toggle",
    key: "unlimited_miles",
    label: "Unlimited mileage",
    order: 70,
    placement: "more",
    param: "unlimited_miles",
    has: (vehicle) => vehicle.unlimited_mileage === true,
  },
  {
    kind: "choice",
    key: "length_max",
    label: "Length",
    order: 80,
    placement: "more",
    param: "length_max",
    anyLabel: "Any length",
    choices: [20, 25, 30, 35].map((n) => ({
      value: n,
      label: `Up to ${n} ft`,
    })),
    matches: (vehicle, value) =>
      vehicle.length_feet != null && vehicle.length_feet <= (value as number),
  },
  {
    kind: "choice",
    key: "year_min",
    label: "Year",
    order: 90,
    placement: "more",
    param: "year_min",
    anyLabel: "Any year",
    choices: [2018, 2020, 2022, 2024].map((n) => ({
      value: n,
      label: `${n} or newer`,
    })),
    matches: (vehicle, value) =>
      vehicle.year != null && vehicle.year >= (value as number),
  },
  {
    kind: "choice",
    key: "booking_mode",
    label: "Booking",
    order: 100,
    placement: "more",
    param: "booking_mode",
    anyLabel: "Any booking mode",
    choices: [
      { value: "instant", label: "Instant book" },
      { value: "request", label: "Request to book" },
    ],
    matches: (vehicle, value) => vehicle.search_booking_mode === value,
  },
];

const definitions = [...FILTERS].sort((a, b) => a.order - b.order);

/** Every URL param this module owns besides the reserved delivery 2 ones. */
export const FILTER_PARAM_KEYS: readonly string[] = [
  ...definitions.flatMap((def) =>
    def.kind === "range" ? [def.paramMin, def.paramMax] : [def.param],
  ),
  "sort",
];

function isActive(def: FilterDefinition, value: FilterValue | undefined) {
  if (value === undefined) return false;
  if (def.kind === "multi") return Array.isArray(value) && value.length > 0;
  if (def.kind === "range") return Array.isArray(value);
  if (def.kind === "choice")
    return typeof value === "number" || typeof value === "string";
  return value === true;
}

function matches(
  def: FilterDefinition,
  value: FilterValue,
  vehicle: VehicleResult,
  ctx: FilterContext,
): boolean {
  if (def.kind === "multi") {
    const picked = value as string[];
    const keys = def.optionsOf(vehicle).map((o) => o.key);
    return def.mode === "all"
      ? picked.every((k) => keys.includes(k))
      : picked.some((k) => keys.includes(k));
  }
  if (def.kind === "range") {
    const [lo, hi] = value as [number, number];
    const price = def.valueOf(vehicle, ctx);
    return price !== null && price >= lo && price <= hi;
  }
  if (def.kind === "choice")
    return def.matches(vehicle, value as string | number);
  return def.has(vehicle);
}

/** Vehicles that satisfy every active filter (AND), optionally ignoring one. */
export function applyFilters(
  vehicles: VehicleResult[],
  selection: FilterSelection,
  ctx: FilterContext,
  exceptKey?: string,
): VehicleResult[] {
  const active = definitions.filter(
    (def) => def.key !== exceptKey && isActive(def, selection[def.key]),
  );
  if (active.length === 0) return vehicles;
  return vehicles.filter((vehicle) =>
    active.every((def) =>
      matches(def, selection[def.key] as FilterValue, vehicle, ctx),
    ),
  );
}

/** Guests are "sleeps at least N"; a vehicle with no `sleeps` stays. */
export function filterBySleeps(
  vehicles: VehicleResult[],
  guests: number,
): VehicleResult[] {
  return vehicles.filter(
    (vehicle) => vehicle.sleeps == null || vehicle.sleeps >= guests,
  );
}

function collectOptions(
  def: MultiDefinition,
  vehicles: VehicleResult[],
): MultiOptionInfo[] {
  const byKey = new Map<string, MultiOptionInfo>();
  for (const vehicle of vehicles) {
    for (const option of def.optionsOf(vehicle)) {
      if (!byKey.has(option.key)) byKey.set(option.key, option);
    }
  }
  const groupIndex = (g: string | null) => {
    const i = def.groups.findIndex((x) => x.group === g);
    return i === -1 ? def.groups.length : i;
  };
  return [...byKey.values()].sort(
    (a, b) =>
      groupIndex(a.group) - groupIndex(b.group) ||
      a.position - b.position ||
      a.name.localeCompare(b.name),
  );
}

function distinctPrices(
  def: RangeDefinition,
  vehicles: VehicleResult[],
  ctx: FilterContext,
): number[] {
  const prices = vehicles
    .map((vehicle) => def.valueOf(vehicle, ctx))
    .filter((p): p is number => p !== null);
  return [...new Set(prices)].sort((a, b) => a - b);
}

function isOffered(count: number, total: number) {
  return count > 0 && count < total;
}

/**
 * Turns what the page address (or the shopper) holds into what applies to
 * these results: values the results do not have are ignored (S11), and
 * defaults the search implies are filled in when the filter is offered (S10).
 * A combination that matches nothing is kept on purpose, so the empty state
 * can offer the way out (S12).
 */
export function resolveSelection(
  raw: FilterSelection,
  vehicles: VehicleResult[],
  ctx: FilterContext,
): FilterSelection {
  const out: FilterSelection = {};
  for (const def of definitions) {
    const value = raw[def.key];
    if (value === undefined) continue;
    if (def.kind === "multi" && Array.isArray(value)) {
      const present = collectOptions(def, vehicles)
        .map((o) => o.key)
        .filter((key) => (value as string[]).includes(key));
      if (present.length > 0) out[def.key] = present;
    } else if (def.kind === "range" && Array.isArray(value)) {
      const prices = distinctPrices(def, vehicles, ctx);
      const [lo, hi] = value as [number, number];
      const loOk = prices.includes(lo);
      const hiOk = prices.includes(hi);
      if (!loOk && !hiOk) continue;
      const range: [number, number] = [
        loOk ? lo : prices[0]!,
        hiOk ? hi : prices[prices.length - 1]!,
      ];
      if (range[0] <= range[1]) out[def.key] = range;
    } else if (def.kind === "toggle" && typeof value === "boolean") {
      if (value === false) out[def.key] = false;
      else if (vehicles.some((v) => def.has(v))) out[def.key] = true;
    } else if (
      def.kind === "choice" &&
      (typeof value === "number" || typeof value === "string")
    ) {
      const known = def.choices.some((c) => c.value === value);
      if (known && vehicles.some((v) => def.matches(v, value))) {
        out[def.key] = value;
      }
    }
  }
  for (const def of definitions) {
    if (def.kind !== "toggle" || !def.defaultOn?.(ctx)) continue;
    if (out[def.key] !== undefined) continue;
    const searchCount = vehicles.filter((v) => def.has(v)).length;
    if (!isOffered(searchCount, vehicles.length)) continue;
    const base = applyFilters(vehicles, out, ctx, def.key);
    if (base.some((v) => def.has(v))) out[def.key] = true;
  }
  return out;
}

export interface FilterOptionView extends MultiOptionInfo {
  count: number;
  selected: boolean;
  /** Would leave no vehicles with the other filters: shown, cannot be picked. */
  disabled: boolean;
}

export interface FilterGroupView {
  group: string;
  label: string;
  count: number;
  /** What ticking the group picks: its options that are not disabled. */
  optionKeys: string[];
  /** None of its options can be picked. */
  disabled: boolean;
}

export interface FilterView {
  key: string;
  kind: FilterKind;
  label: string;
  /** The search results split on it; it stays while the shopper filters. */
  offered: boolean;
  /** Nothing in it can be picked with the other filters, and it is not set. */
  disabled: boolean;
  active: boolean;
  /** What the chip reads: "RV type", "Class A", "RV type (2)", "$100–$250". */
  chipLabel: string;
  /** `more`: lives inside the "More filters" chip. */
  placement: "bar" | "more";
  // multi
  options?: FilterOptionView[];
  groups?: FilterGroupView[];
  /** Vehicles under "All" for this filter (the others applied). */
  allCount?: number;
  // range
  /** Distinct per-night prices of the vehicles left; the handles stop here. */
  points?: number[];
  /** One per-night price per vehicle left, for the histogram. */
  prices?: number[];
  // toggle
  count?: number;
  // choice
  choices?: FilterChoiceView[];
  anyLabel?: string;
}

export interface FilterChoiceView {
  value: string | number;
  label: string;
  count: number;
  selected: boolean;
  disabled: boolean;
}

export function formatPrice(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatRange(range: [number, number], ctx: FilterContext) {
  const [lo, hi] = range;
  return Number.isFinite(hi)
    ? `${formatPrice(lo, ctx.currency)}–${formatPrice(hi, ctx.currency)}`
    : `${formatPrice(lo, ctx.currency)}+`;
}

/** Everything the bar renders, per filter, in bar order. */
export function buildFilterViews(
  vehicles: VehicleResult[],
  selection: FilterSelection,
  ctx: FilterContext,
): FilterView[] {
  return definitions.map((def): FilterView => {
    const value = selection[def.key];
    const active = isActive(def, value);
    const base = applyFilters(vehicles, selection, ctx, def.key);

    if (def.kind === "multi") {
      const picked = (value as string[] | undefined) ?? [];
      const infos = collectOptions(def, vehicles);
      const countIn = (list: VehicleResult[], key: string) =>
        list.filter((v) => def.optionsOf(v).some((o) => o.key === key)).length;
      // The search alone lists the options and their order, so the menu
      // holds still while the shopper filters.
      const listed = infos
        .map((info) => ({ info, searchCount: countIn(vehicles, info.key) }))
        .filter(
          ({ info, searchCount }) =>
            isOffered(searchCount, vehicles.length) ||
            picked.includes(info.key),
        );
      if (def.sortByCount) listed.sort((a, b) => b.searchCount - a.searchCount);
      // With AND, an option is counted among the vehicles that already have
      // every other pick; with OR, among those the other filters leave.
      const optionBase =
        def.mode === "all" ? applyFilters(vehicles, selection, ctx) : base;
      const options = listed.map(({ info }) => {
        const count = countIn(optionBase, info.key);
        const selected = picked.includes(info.key);
        return { ...info, count, selected, disabled: count === 0 && !selected };
      });
      const groups = def.groups.flatMap((g) => {
        const members = options.filter((o) => o.group === g.group);
        if (members.length === 0) return [];
        const optionKeys = members.filter((o) => !o.disabled).map((o) => o.key);
        return [
          {
            group: g.group,
            label: g.label,
            count: members.reduce((sum, o) => sum + o.count, 0),
            optionKeys,
            disabled: optionKeys.length === 0,
          },
        ];
      });
      const names = picked.map(
        (key) => infos.find((i) => i.key === key)?.name ?? key,
      );
      return {
        key: def.key,
        kind: "multi",
        label: def.label,
        offered: options.length > 0,
        disabled: !active && options.every((o) => o.disabled),
        active,
        placement: def.placement ?? "bar",
        chipLabel:
          names.length === 0
            ? def.label
            : names.length === 1 && !def.chipCount
              ? names[0]!
              : `${def.label} (${names.length})`,
        options,
        groups,
        allCount: base.length,
      };
    }

    if (def.kind === "range") {
      const points = distinctPrices(def, base, ctx);
      const prices = base
        .map((v) => def.valueOf(v, ctx))
        .filter((p): p is number => p !== null);
      return {
        key: def.key,
        kind: "range",
        label: def.label,
        placement: def.placement ?? "bar",
        offered: distinctPrices(def, vehicles, ctx).length >= 2 || active,
        disabled: !active && points.length < 2,
        active,
        chipLabel: active
          ? formatRange(value as [number, number], ctx)
          : def.label,
        points,
        prices,
      };
    }

    if (def.kind === "choice") {
      const countIn = (list: VehicleResult[], step: string | number) =>
        list.filter((v) => def.matches(v, step)).length;
      const choices = def.choices
        .filter(
          (c) =>
            isOffered(countIn(vehicles, c.value), vehicles.length) ||
            value === c.value,
        )
        .map((c) => {
          const count = countIn(base, c.value);
          const selected = value === c.value;
          return {
            value: c.value,
            label: c.label,
            count,
            selected,
            disabled: count === 0 && !selected,
          };
        });
      return {
        key: def.key,
        kind: "choice",
        label: def.label,
        placement: def.placement ?? "bar",
        offered: choices.length > 0,
        disabled: !active && choices.every((c) => c.disabled),
        active,
        chipLabel: def.label,
        choices,
        anyLabel: def.anyLabel,
      };
    }

    const count = base.filter((v) => def.has(v)).length;
    const searchCount = vehicles.filter((v) => def.has(v)).length;
    return {
      key: def.key,
      kind: "toggle",
      placement: def.placement ?? "bar",
      label: def.label,
      offered: isOffered(searchCount, vehicles.length) || active,
      disabled: !active && count === 0,
      active,
      chipLabel: def.label,
      count,
    };
  });
}

export interface FilterPill {
  id: string;
  filterKey: string;
  label: string;
}

function describe(
  def: FilterDefinition,
  value: FilterValue,
  vehicles: VehicleResult[],
  ctx: FilterContext,
): string[] {
  if (def.kind === "multi") {
    const infos = collectOptions(def, vehicles);
    return (value as string[]).map(
      (key) => infos.find((i) => i.key === key)?.name ?? key,
    );
  }
  if (def.kind === "range")
    return [formatRange(value as [number, number], ctx)];
  if (def.kind === "choice")
    return [def.choices.find((c) => c.value === value)?.label ?? def.label];
  return [def.label];
}

/** One removable pill per picked type, plus one per other active filter. */
export function buildPills(
  vehicles: VehicleResult[],
  selection: FilterSelection,
  ctx: FilterContext,
): FilterPill[] {
  const pills: FilterPill[] = [];
  for (const def of definitions) {
    const value = selection[def.key];
    if (value === undefined || !isActive(def, value)) continue;
    const labels = describe(def, value, vehicles, ctx);
    if (def.kind === "multi") {
      (value as string[]).forEach((key, i) =>
        pills.push({
          id: `${def.key}:${key}`,
          filterKey: def.key,
          label: labels[i]!,
        }),
      );
    } else {
      pills.push({ id: def.key, filterKey: def.key, label: labels[0]! });
    }
  }
  return pills;
}

export function countActiveFilters(selection: FilterSelection): number {
  return definitions.filter((def) => isActive(def, selection[def.key])).length;
}

function toggleDefaultsOn(def: FilterDefinition, ctx: FilterContext) {
  return def.kind === "toggle" && !!def.defaultOn?.(ctx);
}

/**
 * Drops one filter. A chip that would default back on is recorded as switched
 * off instead, so removing it sticks.
 */
export function removeFilter(
  selection: FilterSelection,
  key: string,
  ctx: FilterContext,
): FilterSelection {
  const next = { ...selection };
  const def = definitions.find((d) => d.key === key);
  if (def && toggleDefaultsOn(def, ctx)) next[key] = false;
  else delete next[key];
  return next;
}

/** Drops one picked option of a menu; the last one removes the filter. */
export function removeOption(
  selection: FilterSelection,
  key: string,
  optionKey: string,
  ctx: FilterContext,
): FilterSelection {
  const rest = ((selection[key] as string[] | undefined) ?? []).filter(
    (k) => k !== optionKey,
  );
  return rest.length === 0
    ? removeFilter(selection, key, ctx)
    : { ...selection, [key]: rest };
}

export function clearFilters(
  selection: FilterSelection,
  ctx: FilterContext,
): FilterSelection {
  return definitions.reduce(
    (next, def) =>
      isActive(def, selection[def.key])
        ? removeFilter(next, def.key, ctx)
        : next,
    selection,
  );
}

/** Menu order for a set of picked option keys. */
export function orderOptionKeys(
  key: string,
  vehicles: VehicleResult[],
  picked: string[],
): string[] {
  const def = definitions.find((d) => d.key === key);
  if (!def || def.kind !== "multi") return picked;
  return collectOptions(def, vehicles)
    .map((o) => o.key)
    .filter((k) => picked.includes(k));
}

export interface BestRemoval {
  key: string;
  label: string;
  count: number;
}

/** The one filter whose removal brings back the most vehicles, if any does. */
export function findBestFilterToRemove(
  vehicles: VehicleResult[],
  selection: FilterSelection,
  ctx: FilterContext,
): BestRemoval | null {
  let best: BestRemoval | null = null;
  for (const def of definitions) {
    const value = selection[def.key];
    if (value === undefined || !isActive(def, value)) continue;
    const count = applyFilters(vehicles, selection, ctx, def.key).length;
    if (count > 0 && (best === null || count > best.count)) {
      const labels = describe(def, value, vehicles, ctx);
      best = {
        key: def.key,
        count,
        label:
          def.kind === "multi" && labels.length > 2
            ? `${def.label} (${labels.length})`
            : labels.join(", "),
      };
    }
  }
  return best;
}

export function sortVehicles(
  vehicles: VehicleResult[],
  sort: SortKey,
  nights: number,
): VehicleResult[] {
  if (sort === "recommended") return vehicles;
  const price = (v: VehicleResult) =>
    pricePerNight(v, nights) ?? Number.POSITIVE_INFINITY;
  const descNullsLast = (
    a: number | null | undefined,
    b: number | null | undefined,
  ) => (b ?? Number.NEGATIVE_INFINITY) - (a ?? Number.NEGATIVE_INFINITY) || 0;
  const compare = {
    price_asc: (a: VehicleResult, b: VehicleResult) => price(a) - price(b),
    price_desc: (a: VehicleResult, b: VehicleResult) => price(b) - price(a),
    newest: (a: VehicleResult, b: VehicleResult) =>
      descNullsLast(a.year, b.year),
    sleeps: (a: VehicleResult, b: VehicleResult) =>
      descNullsLast(a.sleeps, b.sleeps),
  }[sort];
  return [...vehicles].sort(compare);
}

type ParamSource = URLSearchParams | Record<string, string | null | undefined>;

function readParam(source: ParamSource, name: string): string | undefined {
  const raw =
    source instanceof URLSearchParams ? source.get(name) : source[name];
  return raw === null || raw === undefined || raw === "" ? undefined : raw;
}

const SORT_KEYS = SORT_OPTIONS.map((o) => o.key);

/** Reads filters and sort from page address params; unknown values are ignored. */
export function parseFilterParams(source: ParamSource): {
  selection: FilterSelection;
  sort: SortKey;
} {
  const selection: FilterSelection = {};
  for (const def of definitions) {
    if (def.kind === "multi") {
      const keys = readParam(source, def.param)
        ?.split(",")
        .map((k) => k.trim())
        .filter(Boolean);
      if (keys?.length) selection[def.key] = keys;
    } else if (def.kind === "range") {
      const min = Number(readParam(source, def.paramMin));
      const max = Number(readParam(source, def.paramMax));
      const hasMin =
        readParam(source, def.paramMin) !== undefined && Number.isFinite(min);
      const hasMax =
        readParam(source, def.paramMax) !== undefined && Number.isFinite(max);
      if (hasMin || hasMax) {
        selection[def.key] = [
          hasMin ? min : 0,
          hasMax ? max : Number.POSITIVE_INFINITY,
        ];
      }
    } else if (def.kind === "choice") {
      const raw = readParam(source, def.param);
      const choice = def.choices.find((c) => String(c.value) === raw);
      if (choice) selection[def.key] = choice.value;
    } else {
      const raw = readParam(source, def.param);
      if (raw === "1") selection[def.key] = true;
      else if (raw === "0") selection[def.key] = false;
    }
  }
  const sort = readParam(source, "sort");
  return {
    selection,
    sort: SORT_KEYS.includes(sort as SortKey)
      ? (sort as SortKey)
      : "recommended",
  };
}

/** The params the page address should hold; an absent key means "not set". */
export function serializeFilterParams(
  selection: FilterSelection,
  sort: SortKey,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const def of definitions) {
    const value = selection[def.key];
    if (value === undefined) continue;
    if (def.kind === "multi") {
      if (isActive(def, value))
        params[def.param] = (value as string[]).join(",");
    } else if (def.kind === "range") {
      const [lo, hi] = value as [number, number];
      params[def.paramMin] = String(lo);
      if (Number.isFinite(hi)) params[def.paramMax] = String(hi);
    } else if (def.kind === "choice") {
      if (isActive(def, value)) params[def.param] = String(value);
    } else if (value === true) {
      params[def.param] = "1";
    } else if (value === false && def.defaultOn) {
      params[def.param] = "0";
    }
  }
  if (sort !== "recommended") params.sort = sort;
  return params;
}
